import React from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useDispatch } from "react-redux";

import { deleteSaleLot } from "actions";

import { SubtleBadge } from "components/Badge";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { FormCollapse } from "components/Form";
import { Input, OptionTogglerField } from "components/Form/FormikControls";
import { summarizeLots } from "components/KillSheet/lib";
import {
  MultiSaleLotsForm,
  removeFormikSaleLotAtIndex,
} from "components/MultiSaleLotsForm";

import { PricingTypes } from "constants/pricingTypes";

import { formatNumber } from "lib";

import { FormValues, SaleLotWithUnitPrice } from "./types";

const NonSkinSummary = () => {
  const { values } = useFormikContext<FormValues>();
  const { saleLots } = values;

  const {
    totalQuantity,
    totalWeightGrams,
    totalPriceCents,
    dollarsPerHead,
    averageWeightKg,
    averageDollarsPerKilo,
  } = summarizeLots(saleLots);

  return (
    <>
      <Grid item xs={6} md={4}>
        <Input
          disabled
          name="total_quantity"
          align="right"
          label="Total Hd"
          overrideValue={formatNumber(totalQuantity, 0)}
        />
      </Grid>

      <Grid item xs={6} md={4}>
        <Input
          disabled
          name="total_weight"
          align="right"
          label="Total Weight"
          afterSymbol="kg"
          overrideValue={formatNumber(totalWeightGrams / 1000)}
        />
      </Grid>
      <Grid item xs={6} md={4}>
        <Input
          disabled
          name="total_price"
          align="right"
          label="Total Price"
          beforeSymbol="$"
          overrideValue={formatNumber(totalPriceCents / 100)}
        />
      </Grid>
      <Grid item xs={6} md={4}>
        <Input
          disabled
          name="dollarsPerHead"
          align="right"
          label="$/Hd"
          beforeSymbol="$"
          overrideValue={formatNumber(dollarsPerHead)}
        />
      </Grid>

      <Grid item xs={6} md={4}>
        <Input
          disabled
          name="averageWeight"
          align="right"
          label="Avg Weight"
          afterSymbol="kg"
          overrideValue={formatNumber(averageWeightKg)}
        />
      </Grid>

      <Grid item xs={6} md={4}>
        <Input
          disabled
          name="averagePricePerKg"
          align="right"
          beforeSymbol="$"
          label="Avg $/kg"
          overrideValue={averageDollarsPerKilo}
        />
      </Grid>
    </>
  );
};

export const LotForm = ({
  defaultSaleLot = {},
}: {
  defaultSaleLot: Partial<SaleLotWithUnitPrice>;
}) => {
  const dispatch = useDispatch();

  const formikProps = useFormikContext<FormValues>();
  const { values } = formikProps;

  const setPricingType = (pricingTypeId: number | null) => {
    formikProps.setFieldValue(
      "saleLots",
      values.saleLots.map(saleLot => ({
        ...saleLot,
        pricing_type_id: pricingTypeId,
      })),
    );
  };
  const [confirmDeleteSaleLot, setConfirmDeleteSaleLot] = React.useState(null);

  const handleClickDelete = (saleLot, index) => {
    if (!saleLot.id) {
      removeFormikSaleLotAtIndex(
        index,
        values.saleLots,
        defaultSaleLot,
        formikProps.setFieldValue,
      );
    } else {
      setConfirmDeleteSaleLot({ saleLot, index });
    }
  };

  const closeConfirmDialog = () => setConfirmDeleteSaleLot(null);

  const handleDelete = () => {
    const { saleLot, index } = confirmDeleteSaleLot;
    dispatch(deleteSaleLot(saleLot));
    removeFormikSaleLotAtIndex(
      index,
      values.saleLots,
      defaultSaleLot,
      formikProps.setFieldValue,
    );
    closeConfirmDialog();
  };

  return (
    <FormCollapse header={<SubtleBadge>Livestock</SubtleBadge>}>
      <Grid xs={12} item>
        <OptionTogglerField
          name="saleLots[0].pricing_type_id"
          options={PricingTypes.all()}
          onChangeExtra={setPricingType}
        />
      </Grid>
      <MultiSaleLotsForm handleClickDelete={handleClickDelete} />
      <NonSkinSummary />
      <ConfirmDialog
        title={createModalTitle("this item")}
        isOpen={confirmDeleteSaleLot}
        onCancel={closeConfirmDialog}
        onDelete={handleDelete}
      />
    </FormCollapse>
  );
};
